'use client';

import { useEffect, useRef } from 'react';
import { useFetcher } from 'react-router';
import { useAuth } from '@/providers/auth';
import { getCookiebotConsent } from '@/lib/adapters';
import { TrackingConsent } from '@/types/tracking-consent';

const COOKIEBOT_EVENTS = ['CookiebotOnConsentReady', 'CookiebotOnAccept', 'CookiebotOnDecline'] as const;

/**
 * Keeps B2C Commerce tracking (SLAS `dnt` claim + `dw_dnt` cookie) in line with Cookiebot `marketing`
 * consent: `dnt:0` enables person-level tracking (Einstein, personalization, PII in reports), which is
 * profiling, not anonymous statistics. Submits only when Cookiebot has answered and differs from the session.
 */
export function TrackingConsentSync() {
    const sessionConsent = useAuth()?.trackingConsent;
    const { submit } = useFetcher();
    const submittedRef = useRef<TrackingConsent | undefined>(undefined);

    useEffect(() => {
        const sync = () => {
            const consent = getCookiebotConsent();
            if (!consent) {
                return;
            }
            const desired = consent.marketing ? TrackingConsent.Accepted : TrackingConsent.Declined;
            if (desired === sessionConsent || desired === submittedRef.current) {
                return;
            }
            submittedRef.current = desired;
            void submit({ trackingConsent: desired }, { method: 'POST', action: '/action/update-tracking-consent' });
        };

        sync();
        COOKIEBOT_EVENTS.forEach((event) => window.addEventListener(event, sync));
        return () => COOKIEBOT_EVENTS.forEach((event) => window.removeEventListener(event, sync));
    }, [sessionConsent, submit]);

    return null;
}
